/**
 * Types for the Product Page
 * Maps to the Umbraco Content API response for product pages
 */

import type { UmbracoImage, UmbracoLink, ProductPrice } from "./layout";

/**
 * A single image in the product media carousel.
 */
export interface ProductCarouselImage {
  kind: "image";
  id: string;
  url: string;
  name: string;
  altText: string;
  width: number;
  height: number;
}

/**
 * A single video in the product media carousel.
 * Plays inline on hover/click. `thumbnailUrl` is the poster + thumbnail-row image.
 */
export interface ProductCarouselVideo {
  kind: "video";
  id: string;
  url: string;
  thumbnailUrl: string | null;
  altText: string | null;
}

/**
 * Discriminated union of a product media gallery entry (image or video).
 */
export type ProductCarouselItem = ProductCarouselImage | ProductCarouselVideo;

/**
 * Availability band shown as a traffic light beneath the price.
 *
 * The exact quantity is commercially confidential and deliberately never leaves
 * the backend: it classifies the stock figure (0 / 1-200 / 201+) and sends only
 * the band. `null` means show nothing at all — stock is hidden for this audience
 * (the product's "hide stock on B2B / DTC" switches), was never synced from
 * Business Central, or the product is digital and has no warehouse.
 *
 * Values are camelCase because the backend serialises enums via
 * JsonStringEnumConverter(JsonNamingPolicy.CamelCase).
 */
export type StockLevel =
  | "outOfStock"
  | "limitedQuantities"
  | "goodAvailability";

/**
 * Product variant attribute (e.g., color, size)
 */
export interface ProductVariantAttribute {
  id: string;
  type: string;
  key: string;
  keyName: string;
  value: string;
  valueName: string;
}

/**
 * A product variant (e.g., different collar sizes).
 *
 * `name` is the backend's convenience label — the *first* attribute's value
 * name. Use `getVariantLabel` instead of reading it directly: it also covers
 * variants defined by more than one attribute (size AND colour), where a single
 * attribute value is ambiguous.
 *
 * `carouselImages` are plain images (ImageViewModel), NOT gallery items — they
 * carry no `kind` discriminator. Use `variantCarouselItems` to feed the media
 * gallery.
 */
export interface ProductVariant {
  id: string;
  type: string;
  name: string | null;
  sku: string | null;
  attributes: ProductVariantAttribute[];
  /** The variant's own availability band, or null when none is shown. */
  stockLevel: StockLevel | null;
  carouselImages: UmbracoImage[];
}

/**
 * Variants composition — present on product types that implement
 * `productWithVariantsComposition` (today: accessories). Absent, or with an
 * empty `items`, for products sold in a single version.
 */
export interface ProductWithVariantsComposition {
  items: ProductVariant[];
}

/**
 * Specification key-value pair
 */
export interface ProductSpecification {
  key: string;
  value: string;
}

/**
 * Product detail block: specification table
 */
export interface SpecificationBlock {
  id: string;
  type: "specificationBlock";
  subtitle?: string | null;
  specifications: ProductSpecification[];
}

/**
 * Product detail block: rich text with heading
 */
export interface RichTextWithHeadingBlock {
  id: string;
  type: "richTextWithHeading";
  heading: string;
  text: string;
  badge?: string | null;
  alignment?: "center" | "left" | null;
}

/**
 * Product detail block: text with linkable images
 */
export interface TextAndLinkableImagesBlock {
  id: string;
  type: "textAndLinkableImagesBlock";
  textHeading: string;
  text: string;
  mainImage: UmbracoImage | null;
  imageLinks: unknown[];
}

/**
 * Single item in a key-benefits strip (GROW-302 iteration 2)
 */
export interface KeyBenefitItem {
  id: string;
  icon: UmbracoImage | null;
  heading: string;
  subtext: string | null;
}

/**
 * Product detail block: key-benefits strip (icon + heading + subtext × N)
 */
export interface KeyBenefitsBlock {
  id: string;
  type: "keyBenefitsBlock";
  items: KeyBenefitItem[];
}

/**
 * Product detail block: feature deep dive (image side-by-side with copy)
 */
export interface FeatureDeepDiveBlock {
  id: string;
  type: "featureDeepDiveBlock";
  image: UmbracoImage | null;
  imagePosition: "left" | "right";
  statBig?: string | null;
  statLabel?: string | null;
  eyebrow: string | null;
  heading: string | null;
  text: string;
  bulletPoints: string[];
}

/**
 * Product detail block: positional marker for "What's in the box".
 * Data is sourced from ProductPageData.includedProducts.
 */
export interface IncludedProductsBlock {
  id: string;
  type: "includedProductsBlock";
  heading?: string | null;
  subheading?: string | null;
}

/**
 * Product detail block: cross-promo band (image + copy + CTA, mint-green bg)
 */
export interface CrossPromoBlock {
  id: string;
  type: "crossPromoBlock";
  image: UmbracoImage | null;
  imagePosition: "left" | "right";
  heading: string | null;
  text: string;
  ctaLink: UmbracoLink | null;
}

/**
 * Product detail block: positional marker for the upsell-products section.
 * Data is sourced from ProductPageData.upsellProducts.
 */
export interface UpsellProductsBlock {
  id: string;
  type: "upsellProductsBlock";
  heading?: string | null;
  subheading?: string | null;
}

/**
 * FAQ block + item types — defined in content.ts as page-level blocks (so they
 * can also be placed in the Block Grid). Re-exported here because they remain
 * members of the ProductDetailBlock union as well.
 */
export type { FaqBlock, FaqItem } from "./content";
import type { FaqBlock } from "./content";

export type ProductDetailBlock =
  | SpecificationBlock
  | RichTextWithHeadingBlock
  | TextAndLinkableImagesBlock
  | KeyBenefitsBlock
  | FeatureDeepDiveBlock
  | IncludedProductsBlock
  | CrossPromoBlock
  | UpsellProductsBlock
  | FaqBlock;

/**
 * Single key point shown in the trust strip above the subscription note.
 * `icon` is a free-form identifier — wiring to a real icon library is a follow-up.
 */
export interface KeyPointItem {
  id: string;
  icon: string;
  text: string;
}

/**
 * Product content composition from CMS
 */
export interface ProductContentComposition {
  sellingLine: string;
  note: string | null;
  description: string;
  carouselItems: ProductCarouselItem[];
  features: string[];
  keyPoints: KeyPointItem[];
  productDetailBlocks: ProductDetailBlock[];
  mainCategory: {
    id: string;
    name: string;
    link: UmbracoLink;
    image: UmbracoImage | null;
  } | null;
  /**
   * Bundle offers configured to show on this product. Null when there are none.
   */
  bundles: ProductBundles | null;
}

/**
 * One product included in a bundle. Mirrors the backend's ItemCardViewModel, so
 * only the fields the bundle card renders are declared.
 */
export interface BundledProduct {
  id: string;
  type: string;
  header: string;
  description: string;
  image: UmbracoImage | null;
  link: UmbracoLink | null;
  price: ProductPrice | null;
  sku: string | null;
  productReference: string | null;
  variants: ProductVariant[];
}

/**
 * A bundle offer: a product with no SKU of its own whose price is the sum of the
 * products it contains. Adding it puts one cart line per included product in the
 * cart; a discount rule then makes the promised items free.
 */
export interface BundleOffer {
  id: string;
  type: string;
  name: string;
  /** Navigation composition — carries the link to the bundle's own product page. */
  compositions?: {
    navigation?: { link: UmbracoLink | null };
  };
  /** Combined price of everything in the bundle, before any bundle discount. */
  price: ProductPrice | null;
  image: UmbracoImage | null;
  /** Rich-text bundle blurb. Already sanitised server-side, but re-sanitise on render. */
  description: string | null;
  bundledProducts: BundledProduct[];
  /** Localised "Includes" label from the CMS dictionary. */
  includesLabel: string;
  /** Localised "Bundle" label from the CMS dictionary. */
  bundleLabel: string;
}

export interface ProductBundles {
  items: BundleOffer[];
  /** Localised "Bundle" heading label from the CMS dictionary. */
  bundleLabel: string;
}

/**
 * Benefit item displayed in the delivery info section
 */
export interface ProductBenefit {
  icon: string;
  text: string;
}

/**
 * Item card used by the accessories and upsell-products lists on a product page.
 */
export interface ProductItemCard {
  id: string;
  type: string;
  image: UmbracoImage | null;
  header: string;
  description: string;
  highlightedText: string;
  link: UmbracoLink;
  price: ProductPrice;
  productCategory: string;
  variants: ProductVariant[];
  sku: string;
  productReference: string;
}

/**
 * Included product (e.g., SIM card bundled with device)
 */
export interface IncludedProduct {
  id: string;
  type: string;
  name: string;
  image: UmbracoImage | null;
  compositions: {
    product?: {
      price: ProductPrice;
      sku: string;
      productReference: string;
      stockLevel: StockLevel | null;
      productType: string | null;
    };
    productContent?: {
      sellingLine: string;
      description: string;
    };
  };
}

/**
 * Full product page data from the content API
 */
export interface ProductPageData {
  id: string;
  type: string;
  name: string;
  image: UmbracoImage;
  includedProducts: IncludedProduct[];
  includesLabel: string;
  /**
   * The products a BUNDLE page contains. Empty on ordinary product pages — a
   * bundle has no SKU of its own, so this list is what actually gets bought.
   */
  bundledProducts?: ProductItemCard[];
  accessories: ProductItemCard[];
  upsellProducts: ProductItemCard[];
  extraSubscriptionsInfoPageLinkViewModel: UmbracoLink | null;
  compositions: {
    metaData?: unknown;
    contentBlocks?: { blocks: unknown[] };
    blockGridLayout?: {
      gridColumns: number;
      items: unknown[];
    };
    product?: {
      price: ProductPrice;
      sku: string;
      productReference: string;
      stockLevel: StockLevel | null;
      productType: string | null;
    };
    navigation?: {
      breadcrumbs: UmbracoLink[];
      link: UmbracoLink;
      image: UmbracoImage;
    };
    productContent?: ProductContentComposition;
    productWithVariants?: ProductWithVariantsComposition | null;
  };
}

/** Umbraco content-type alias of a bundle product (uSync: bundleproduct.config). */
export const BUNDLE_PRODUCT_TYPE = "bundleProduct";

/**
 * Type guard to check if content is a product page.
 *
 * A bundle counts: it has its own page and its own add-to-cart, it just resolves
 * to several order lines instead of one. Leaving it out rendered bundle pages
 * through the generic CMS path, with no ProductProvider and no buy button.
 */
export function isProductPage(
  content: { type: string } | null | undefined
): content is ProductPageData {
  return (
    content?.type === "device" ||
    content?.type === "accessory" ||
    content?.type === "license" ||
    content?.type === BUNDLE_PRODUCT_TYPE
  );
}

/**
 * True for a bundle page, whose add-to-cart must go to POST /cart/bundle. Posting
 * a bundle to the ordinary endpoint is rejected — it has no price of its own.
 */
export function isBundleProductPage(
  product: { type?: string } | null | undefined
): boolean {
  return product?.type === BUNDLE_PRODUCT_TYPE;
}

/**
 * The products a bundle page will actually add to the cart. Falls back to the
 * bundle offer list shape so both the page and the suggestion card can share it.
 */
export function bundledProductsOf(
  product: Pick<ProductPageData, "bundledProducts"> | null | undefined
): ProductItemCard[] {
  return product?.bundledProducts ?? [];
}

/**
 * Variants of a product page, or an empty list when the product is sold in a
 * single version (no variants composition, or an empty one).
 */
export function getProductVariants(
  product: Pick<ProductPageData, "compositions"> | null | undefined
): ProductVariant[] {
  return product?.compositions?.productWithVariants?.items ?? [];
}

/**
 * Human-readable label for a variant option button.
 *
 * A variant defined by several attributes (e.g. size AND colour) is labelled
 * with every attribute value — the backend's `name` only carries the first one,
 * which would render two different variants identically.
 */
export function getVariantLabel(variant: ProductVariant): string {
  const attributeValues = (variant.attributes ?? [])
    .map((attribute) => attribute.valueName?.trim())
    .filter((value): value is string => !!value);

  if (attributeValues.length > 1) return attributeValues.join(" / ");

  return (
    variant.name?.trim() ||
    attributeValues[0] ||
    variant.sku?.trim() ||
    variant.id
  );
}

/**
 * Variant images as media-gallery entries. Variant images are plain
 * `ImageViewModel`s server-side, so the `kind` discriminator the gallery
 * switches on has to be added here.
 */
export function variantCarouselItems(
  variant: ProductVariant | null | undefined
): ProductCarouselImage[] {
  return (variant?.carouselImages ?? []).map((image) => ({
    kind: "image" as const,
    id: image.id,
    url: image.url,
    name: image.name,
    altText: image.altText,
    width: image.width,
    height: image.height,
  }));
}

/**
 * Backend ProductType constants. Mirrors Tracker.Application.Common.Constants.ProductTypes.
 */
export const PRODUCT_TYPE_DEVICE_SUBSCRIPTION = "device-subscription";

/**
 * Returns true when the product page represents a device-subscription product
 * (a recurring SIM/data plan that has to be bound to one of the user's
 * existing devices at add-to-cart time).
 */
export function isDeviceSubscriptionProduct(
  product: Pick<ProductPageData, "compositions"> | null | undefined
): boolean {
  return (
    product?.compositions?.product?.productType ===
    PRODUCT_TYPE_DEVICE_SUBSCRIPTION
  );
}
